// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "./userStorage";
/**
 * Journal Service
 * 
 * Provides centralized functionality for managing journal entries across the application.
 * Handles data persistence through localStorage with JSON file fallback.
 */

// Default journal data in case the local storage is empty
import defaultJournalData from '../data/journal.json';

// Constants
const STORAGE_KEY = 'prayer_app_journal';
const MAX_SEARCH_RESULTS = 50;
const DEMO_ENTRY_IDS = new Set([
  '1682121600000',
  '1681689600000',
  '1681257600000',
  '1680825600000',
  '1680566400000',
  '1680134400000',
  '1679702400000'
]);

const removeDemoEntries = (entries) => entries.filter((entry) => !DEMO_ENTRY_IDS.has(String(entry.id)));

/**
 * Initialize journal storage with data from JSON if not already initialized
 * @returns {Array} Array of journal entries
 */
export const initializeJournal = () => {
  try {
    // Check if data exists in localStorage
    const storedData = localStorage.getItem(STORAGE_KEY);
    
    if (!storedData) {
      // If not, initialize with default data from JSON file
      const entries = removeDemoEntries(defaultJournalData.entries || []);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ entries }));
      return entries;
    }
    
    // Parse and return stored data
    const parsedData = JSON.parse(storedData);
    const entries = removeDemoEntries(parsedData.entries || []);
    if (entries.length !== (parsedData.entries || []).length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ entries }));
    }
    return entries;
  } catch (error) {
    console.error('Error initializing journal:', error);
    return [];
  }
};

/**
 * Get all journal entries
 * @returns {Array} Array of journal entries
 */
export const getAllEntries = () => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    
    if (!storedData) {
      return initializeJournal();
    }
    
    const parsedData = JSON.parse(storedData);
    const entries = removeDemoEntries(parsedData.entries || []);
    if (entries.length !== (parsedData.entries || []).length) saveEntries(entries);
    return entries;
  } catch (error) {
    console.error('Error getting journal entries:', error);
    return [];
  }
};

/**
 * Save the full list of entries
 * @param {Array} entries - Array of journal entries
 * @returns {boolean} Success status
 */
const saveEntries = (entries) => {
  try {
    const journalData = { entries };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(journalData));
    return true;
  } catch (error) {
    console.error('Error saving journal entries:', error);
    return false;
  }
};

/**
 * Add a new journal entry
 * @param {Object} entry - The entry to add
 * @returns {Object} The added entry with ID and date
 */
export const addEntry = (entry) => {
  try {
    const entries = getAllEntries();
    
    // Create a new entry with ID and date if not provided
    const now = new Date();
    const newEntry = {
      id: entry.id || now.getTime().toString(),
      date: entry.date || now.toISOString(),
      title: entry.title || 'Untitled Entry',
      content: entry.content || '',
      tags: entry.tags || [],
      relatedScripture: entry.relatedScripture || '',
      prayerGuideId: entry.prayerGuideId || null,
      day: entry.day || null,
      lastEdited: now.toISOString()
    };
    
    // Add to beginning of array (newest first)
    const updatedEntries = [newEntry, ...entries];
    
    // Save to localStorage
    saveEntries(updatedEntries);
    
    return newEntry;
  } catch (error) {
    console.error('Error adding journal entry:', error);
    throw error;
  }
};

/**
 * Update an existing journal entry
 * @param {string} id - ID of the entry to update
 * @param {Object} updates - Fields to update
 * @returns {Object|null} Updated entry or null if not found
 */
export const updateEntry = (id, updates) => {
  try {
    const entries = getAllEntries();
    const entryIndex = entries.findIndex(entry => entry.id === id);
    
    if (entryIndex === -1) {
      return null; // Entry not found
    }
    
    // Update entry with new values
    const updatedEntry = {
      ...entries[entryIndex],
      ...updates,
      lastEdited: new Date().toISOString()
    };
    
    entries[entryIndex] = updatedEntry;
    
    // Save to localStorage
    saveEntries(entries);
    
    return updatedEntry;
  } catch (error) {
    console.error('Error updating journal entry:', error);
    throw error;
  }
};

/**
 * Delete a journal entry
 * @param {string} id - ID of the entry to delete
 * @returns {boolean} Success status
 */
export const deleteEntry = (id) => {
  try {
    const entries = getAllEntries();
    const filteredEntries = entries.filter(entry => entry.id !== id);
    
    // Check if any entry was removed
    if (filteredEntries.length === entries.length) {
      return false; // No entry was removed
    }
    
    // Save updated entries
    saveEntries(filteredEntries);
    
    return true;
  } catch (error) {
    console.error('Error deleting journal entry:', error);
    throw error;
  }
};

/**
 * Get a specific journal entry by ID
 * @param {string} id - ID of the entry to retrieve
 * @returns {Object|null} The entry or null if not found
 */
export const getEntryById = (id) => {
  try {
    const entries = getAllEntries();
    return entries.find(entry => entry.id === id) || null;
  } catch (error) {
    console.error('Error getting journal entry:', error);
    return null;
  }
};

/**
 * Get entries related to a specific prayer guide
 * @param {string} prayerGuideId - ID of the prayer guide
 * @returns {Array} Entries related to the prayer guide
 */
export const getEntriesByPrayerGuide = (prayerGuideId) => {
  try {
    const entries = getAllEntries();
    return entries.filter(entry => entry.prayerGuideId === prayerGuideId);
  } catch (error) {
    console.error('Error getting entries by prayer guide:', error);
    return [];
  }
};

/**
 * Get entries for a specific day of a prayer guide
 * @param {string} prayerGuideId - ID of the prayer guide
 * @param {number} day - Day number
 * @returns {Array} Entries for the specified day
 */
export const getEntriesByPrayerGuideDay = (prayerGuideId, day) => {
  try {
    const entries = getAllEntries();
    return entries.filter(entry => 
      entry.prayerGuideId === prayerGuideId && entry.day === day
    );
  } catch (error) {
    console.error('Error getting entries by prayer guide day:', error);
    return [];
  }
};

/**
 * Get entries by tag
 * @param {string} tag - Tag to filter by
 * @returns {Array} Entries with the specified tag
 */
export const getEntriesByTag = (tag) => {
  try {
    const entries = getAllEntries();
    return entries.filter(entry => entry.tags && entry.tags.includes(tag));
  } catch (error) {
    console.error('Error getting entries by tag:', error);
    return [];
  }
};

/**
 * Get entries by date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Array} Entries within the date range
 */
export const getEntriesByDateRange = (startDate, endDate) => {
  try {
    const entries = getAllEntries();
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();
    
    return entries.filter(entry => {
      const entryTime = new Date(entry.date).getTime();
      return entryTime >= startTime && entryTime <= endTime;
    });
  } catch (error) {
    console.error('Error getting entries by date range:', error);
    return [];
  }
};

/**
 * Search entries by keyword in title or content
 * @param {string} keyword - Keyword to search for
 * @returns {Array} Matching entries
 */
export const searchEntries = (keyword) => {
  try {
    if (!keyword || keyword.trim() === '') {
      return [];
    }
    
    const entries = getAllEntries();
    const lowercaseKeyword = keyword.toLowerCase();
    
    // Search in title, content, tags, and relatedScripture
    const results = entries.filter(entry => 
      (entry.title && entry.title.toLowerCase().includes(lowercaseKeyword)) ||
      (entry.content && entry.content.toLowerCase().includes(lowercaseKeyword)) ||
      (entry.tags && entry.tags.some(tag => tag.toLowerCase().includes(lowercaseKeyword))) ||
      (entry.relatedScripture && entry.relatedScripture.toLowerCase().includes(lowercaseKeyword))
    );
    
    // Limit results
    return results.slice(0, MAX_SEARCH_RESULTS);
  } catch (error) {
    console.error('Error searching entries:', error);
    return [];
  }
};

/**
 * Get all unique tags from journal entries
 * @returns {Array} Array of unique tags
 */
export const getAllTags = () => {
  try {
    const entries = getAllEntries();
    const allTags = entries.reduce((tags, entry) => {
      if (entry.tags && Array.isArray(entry.tags)) {
        return [...tags, ...entry.tags];
      }
      return tags;
    }, []);
    
    // Remove duplicates
    return [...new Set(allTags)];
  } catch (error) {
    console.error('Error getting all tags:', error);
    return [];
  }
};

/**
 * Export journal entries as JSON
 * @returns {string} JSON string of all entries
 */
export const exportJournal = () => {
  try {
    const entries = getAllEntries();
    return JSON.stringify({ entries }, null, 2);
  } catch (error) {
    console.error('Error exporting journal:', error);
    throw error;
  }
};

/**
 * Import journal entries from JSON
 * @param {string} jsonData - JSON string of entries to import
 * @returns {Array} The imported entries
 */
export const importJournal = (jsonData) => {
  try {
    const data = JSON.parse(jsonData);
    
    if (!data.entries || !Array.isArray(data.entries)) {
      throw new Error('Invalid journal data format');
    }
    
    // Save the imported entries
    saveEntries(data.entries);
    
    return data.entries;
  } catch (error) {
    console.error('Error importing journal:', error);
    throw error;
  }
};

export default {
  initializeJournal,
  getAllEntries,
  addEntry,
  updateEntry,
  deleteEntry,
  getEntryById,
  getEntriesByPrayerGuide,
  getEntriesByPrayerGuideDay,
  getEntriesByTag,
  getEntriesByDateRange,
  searchEntries,
  getAllTags,
  exportJournal,
  importJournal
};
